import { useNavigate } from 'react-router';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { Icon } from '@/kit/Icon';
import { notifyError } from '@/core/notify/notify';
import { filingService } from '@/core/services/filing';
import type { FilingHistoryRow } from '@/core/models/filing.models';
import { fetchedLabel } from './TaxreturnIntegrations';
import { guardClient, useLiveState, useOnInit, useSelectedClient } from './shared';
import './Integrations.scss';

/** Companies House filing history with a per-row document download (Angular TaxreturnIntegrationsHistoryComponent). */
export function TaxreturnIntegrationsHistory() {
  const navigate = useNavigate();
  const client = useSelectedClient();
  const [s, set] = useLiveState<{ dataSource: FilingHistoryRow[]; fetchedLabel: string }>({
    dataSource: [],
    fetchedLabel: '',
  });

  useOnInit(() => {
    if (!guardClient(client, navigate)) {
      return;
    }
    filingService.getFilingHistory(client.id).then(
      (result) => {
        set({ dataSource: result.items, fetchedLabel: fetchedLabel(result) });
        if (result.refreshError) {
          notifyError(`Showing the last fetched data. ${result.refreshError}`);
        }
      },
      (error) => notifyError(error),
    );
  });

  function download(row: FilingHistoryRow): void {
    if (!client || !row.hasDocument) {
      return;
    }
    filingService.downloadFilingDocument(client.id, row.transactionId).then(undefined, (error) => notifyError(error));
  }

  const columns: DataTableColumn<FilingHistoryRow>[] = [
    { id: 'date', header: 'Date', cell: (row) => row.date },
    { id: 'description', header: 'Description', cell: (row) => row.description },
    {
      id: 'download',
      header: 'Download',
      cell: (row) =>
        row.hasDocument && (
          <Icon
            name="file_download"
            outlined
            className="text-primary cursor-pointer"
            role="button"
            tabIndex={0}
            title="Download"
            aria-label="Download"
            aria-hidden={undefined}
            onClick={() => download(row)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                download(row);
              }
            }}
          />
        ),
    },
  ];

  return (
    <SideMenuPage menu="integration" contentClassName="tr-integrations">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <p className="subtitle">List of Filling History : {s.fetchedLabel}</p>
          </div>
        </div>
        <div className="mat-table-wrapper no-hover">
          <DataTable stickyHeader columns={columns} rows={s.dataSource} rowKey={(_, index) => index} />
        </div>
      </div>
    </SideMenuPage>
  );
}
